import { useEffect, useMemo, useState } from "react";
import { Clock4, Loader2, Pencil, Plus, Save, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, isLeader } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const L = ({ children }) => (
  <Label className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500">{children}</Label>
);

const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const hm = (secs) => {
  const s = Math.max(0, Math.floor(secs || 0));
  return `${Math.floor(s / 3600)} h ${String(Math.floor((s % 3600) / 60)).padStart(2, "0")} min`;
};

const emptyForm = (userId) => ({
  id: null, user_id: userId || "", date: todayLocal(), hours: "", minutes: "",
  kind: "work", note: "", job_id: "",
});

export const TimeEntriesPanel = ({ job, staff = [], onSaved, readOnly, workOnly }) => {
  const { user } = useAuth();
  const leader = isLeader(user?.role);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [warn, setWarn] = useState(null);
  const [removeId, setRemoveId] = useState(null);
  const [jobs, setJobs] = useState([]);

  const entries = useMemo(
    () => [...(job.time_entries || [])].sort(
      (a, b) => `${b.date}${b.created_at || ""}`.localeCompare(`${a.date}${a.created_at || ""}`),
    ),
    [job.time_entries],
  );

  useEffect(() => {
    if (!leader) return;
    api.get("/jobs").then(({ data }) => setJobs(data)).catch(() => {});
  }, [leader]);

  const people = leader ? staff : staff.filter((s) => s.id === user?.id);

  const payloadOf = (f, force) => ({
    user_id: f.user_id || user?.id,
    date: f.date,
    hours: parseInt(f.hours, 10) || 0,
    minutes: parseInt(f.minutes, 10) || 0,
    kind: f.kind,
    note: f.note || null,
    job_id: f.job_id || null,
    force: !!force,
  });

  const submit = async (force = false) => {
    setBusy(true);
    try {
      if (form.id) await api.put(`/jobs/${job.id}/time-entries/${form.id}`, payloadOf(form, force));
      else await api.post(`/jobs/${job.id}/time-entries`, payloadOf(form, force));
      toast.success(form.id ? "Tidsregistreringen är uppdaterad" : "Tid tillagd");
      setWarn(null);
      setForm(null);
      await onSaved?.();
    } catch (e) {
      if (e?.response?.status === 409) {
        setWarn(e.response.data?.detail || "Överlappande tid");
      } else {
        toast.error(apiError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/jobs/${job.id}/time-entries/${removeId}`);
      toast.success("Tidsregistreringen är borttagen");
      setRemoveId(null);
      await onSaved?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const total = entries.reduce((s, e) => s + (e.seconds || 0), 0);

  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-white p-4" data-testid="time-entries-panel">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="flex flex-1 items-center gap-2 text-xl font-semibold text-slate-800">
          <Clock4 className="h-4 w-4 text-slate-400" /> Tidsregistreringar
        </h2>
        {!form && !readOnly && (
          <Button
            data-testid="time-entry-add-button"
            onClick={() => setForm(emptyForm(user?.id))}
            className="h-10 bg-blue-600 text-white transition-colors duration-200 hover:bg-blue-700"
          >
            <Plus className="mr-1.5 h-4 w-4" /> Lägg till tid
          </Button>
        )}
      </div>
      <p className="mt-1 text-sm text-slate-600">
        Manuellt registrerad arbets- och restid. {leader
          ? "Du kan registrera tid åt vilken tekniker som helst."
          : "Du kan registrera tid på ditt eget konto."}
      </p>

      {entries.length === 0 ? (
        <p className="mt-3 text-sm font-medium text-slate-500" data-testid="time-entries-empty">
          Inga manuella tidsregistreringar.
        </p>
      ) : (
        <div className="mt-3 overflow-hidden rounded-md border border-slate-200" data-testid="time-entries-list">
          {entries.map((e) => (
            <div
              key={e.id}
              data-testid={`time-entry-${e.id}`}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-slate-100 p-3 text-sm last:border-0"
            >
              <span className="font-semibold text-slate-900">{e.user_name || "Tekniker"}</span>
              <span className="text-slate-700">{e.date}</span>
              <span className={`rounded border px-1.5 py-0.5 text-[11px] font-semibold ${
                e.kind === "travel"
                  ? "border-sky-200 bg-sky-50 text-sky-800"
                  : "border-emerald-200 bg-emerald-50 text-emerald-800"
              }`}>
                {e.kind === "travel" ? "Restid" : "Arbetstid"}
              </span>
              <span className="text-slate-600">{hm(e.seconds)}</span>
              {e.note && <span className="min-w-0 flex-1 truncate text-slate-500">{e.note}</span>}
              {!readOnly && (
                <span className="ml-auto flex gap-1">
                  <button
                    data-testid={`time-entry-edit-${e.id}`}
                    onClick={() => setForm({
                      id: e.id, user_id: e.user_id, date: e.date,
                      hours: String(Math.floor((e.seconds || 0) / 3600)),
                      minutes: String(Math.floor(((e.seconds || 0) % 3600) / 60)),
                      kind: e.kind, note: e.note || "", job_id: "",
                    })}
                    className="rounded p-2 text-slate-600 transition-colors duration-200 hover:bg-slate-100"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    data-testid={`time-entry-delete-${e.id}`}
                    onClick={() => setRemoveId(e.id)}
                    className="rounded p-2 text-red-600 transition-colors duration-200 hover:bg-red-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </span>
              )}
            </div>
          ))}
          <div className="flex justify-between bg-slate-50 p-3 text-sm font-semibold text-slate-800">
            <span>Summa manuell tid (ingår i orderns totaltid)</span>
            <span data-testid="time-entries-total">{hm(total)}</span>
          </div>
        </div>
      )}

      {form && (
        <div className="mt-3 space-y-3 rounded-md border border-blue-200 bg-blue-50/50 p-3" data-testid="time-entry-form">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <L>Tekniker / användare</L>
              <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })} disabled={!leader}>
                <SelectTrigger data-testid="time-entry-user-select" className="mt-1 h-10 bg-white">
                  <SelectValue placeholder="Välj tekniker" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {people.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`time-entry-user-option-${s.id}`}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <L>Datum</L>
              <Input
                type="date"
                data-testid="time-entry-date-input"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="mt-1 h-10 bg-white"
              />
            </div>
            {!workOnly && (
            <div>
              <L>Typ</L>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="time-entry-kind-select" className="mt-1 h-10 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="work" data-testid="time-entry-kind-work">Arbetstid</SelectItem>
                  <SelectItem value="travel" data-testid="time-entry-kind-travel">Restid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            )}
            <div>
              <L>Timmar</L>
              <Input
                data-testid="time-entry-hours-input"
                inputMode="numeric"
                placeholder="2"
                value={form.hours}
                onChange={(e) => setForm({ ...form, hours: e.target.value })}
                className="mt-1 h-10 bg-white"
              />
            </div>
            <div>
              <L>Minuter</L>
              <Input
                data-testid="time-entry-minutes-input"
                inputMode="numeric"
                placeholder="30"
                value={form.minutes}
                onChange={(e) => setForm({ ...form, minutes: e.target.value })}
                className="mt-1 h-10 bg-white"
              />
            </div>
            <div>
              <L>Anteckning</L>
              <Input
                data-testid="time-entry-note-input"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="t.ex. glömde starta ordern"
                className="mt-1 h-10 bg-white"
              />
            </div>
            {form.id && leader && (
              <div className="sm:col-span-2 lg:col-span-3">
                <L>Kopplad order (flytta tiden)</L>
                <Select value={form.job_id} onValueChange={(v) => setForm({ ...form, job_id: v })}>
                  <SelectTrigger data-testid="time-entry-job-select" className="mt-1 h-10 bg-white">
                    <SelectValue placeholder="Behåll denna order" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {jobs.map((j) => (
                      <SelectItem key={j.id} value={j.id} data-testid={`time-entry-job-option-${j.id}`}>
                        #{String(j.order_number || "").padStart(4, "0")} {j.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="time-entry-save-button"
              disabled={busy || !form.user_id}
              onClick={() => submit(false)}
              className="h-11 bg-blue-600 text-white transition-colors duration-200 hover:bg-blue-700"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" /> Spara</>}
            </Button>
            <Button
              data-testid="time-entry-cancel-button"
              variant="outline"
              disabled={busy}
              onClick={() => setForm(null)}
              className="h-11 bg-white transition-colors duration-200 hover:bg-slate-100"
            >
              <X className="mr-1.5 h-4 w-4" /> Avbryt
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={!!warn} onOpenChange={(o) => !o && setWarn(null)}>
        <AlertDialogContent className="bg-white" data-testid="time-entry-overlap-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Teknikern har redan registrerad tid eller en bokning denna dag. Vill du fortsätta ändå?
            </AlertDialogTitle>
            <AlertDialogDescription data-testid="time-entry-overlap-detail">{warn}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="time-entry-overlap-cancel">Avbryt</AlertDialogCancel>
            <AlertDialogAction
              data-testid="time-entry-overlap-ok"
              disabled={busy}
              onClick={(e) => { e.preventDefault(); submit(true); }}
              className="bg-blue-600 text-white hover:bg-blue-700"
            >
              Fortsätt ändå
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!removeId} onOpenChange={(o) => !o && setRemoveId(null)}>
        <AlertDialogContent className="bg-white" data-testid="time-entry-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Är du säker på att du vill ta bort denna tidsregistrering?</AlertDialogTitle>
            <AlertDialogDescription>
              Posten tas bort från arbetsordern och ändringen loggas i ändringshistoriken.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="time-entry-delete-cancel">Avbryt</AlertDialogCancel>
            <AlertDialogAction
              data-testid="time-entry-delete-ok"
              disabled={busy}
              onClick={(e) => { e.preventDefault(); remove(); }}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Ta bort
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
